import { ButtonHTMLAttributes, FC, forwardRef, RefAttributes } from "react";
import {
  textFontClassName,
  transitionClassName,
  btnSizeClassName,
  visibilityClassName,
  getFocusOutline,
} from "./commonClassNames";
import { ColorProps, GroupedProp, SizeProp, TextFontProps, VisibilityProp } from "./commonProps";
import { Spinner } from "./Spinner";

type ButtonColorProps = Pick<ColorProps, "transparent" | "green" | "primary" | "red" | "lightBlue" | "blue"> &
  Partial<{
    outlined: boolean;
    inverse: boolean;
    primaryGradient: boolean;
  }>;

export type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> &
  RefAttributes<HTMLButtonElement> &
  ButtonColorProps &
  GroupedProp &
  SizeProp &
  Pick<TextFontProps, "uppercase" | "font"> &
  VisibilityProp & {
    loading?: boolean;
    className?: string;
    contentAlign?: "start" | "center" | "end";
    fill?: boolean;
  };

export const Button: FC<ButtonProps> = forwardRef(
  (
    {
      disabled,
      font,
      green,
      grouped,
      invisible,
      lightBlue,
      loading,
      outlined,
      primary,
      primaryGradient,
      red,
      size,
      transparent,
      uppercase,
      children,
      className,
      blue,
      inverse,
      onClick,
      contentAlign,
      fill,
      ...props
    },
    ref,
  ) => {
    const baseClassName = [
      "select-none shadow flex flex-row items-center",
      contentAlign === "start"
        ? "justify-start"
        : contentAlign === "center" || contentAlign === undefined
        ? "justify-center"
        : "justify-right",
      "rounded-[30px]",
      fill ? "w-full" : "",
      loading ? "cursor-default" : "",
      disabled ? "cursor-not-allowed" : "",
      btnSizeClassName({ size }),
      buttonColorClassName({
        green,
        lightBlue,
        outlined,
        primary,
        primaryGradient,
        red,
        blue,
        transparent,
        inverse,
      }),
      textFontClassName({ font, uppercase }),
      transitionClassName({ colors: true }),
      visibilityClassName({ invisible }),
      getFocusOutline({ border: red ? "red" : green ? "green" : primary ? "primary" : undefined }),
      disabled ? "opacity-50" : "",
      className ?? "",
    ].join(" ");

    const generatedClassName = `${baseClassName.trim()}${className ? ` ${className.trim()}` : ""}`;

    return (
      <button
        {...props}
        className={generatedClassName}
        disabled={disabled}
        ref={ref}
        onClick={disabled ? undefined : onClick}
      >
        {loading ? <Spinner /> : <>{children}</>}
      </button>
    );
  },
);

function buttonColorClassName({
  outlined,
  green,
  lightBlue,
  primary,
  red,
  blue,
  transparent,
  primaryGradient,
  inverse,
}: ButtonColorProps) {
  switch (true) {
    case outlined && green:
      return "border border-green-400 bg-transparent text-green-400";
    case outlined && primary:
      return "border border-primary-200 bg-transparent text-primary-200 dark:text-primary-400";
    case outlined && red:
      return "border border-red-400 bg-transparent text-red-400";
    case outlined && blue:
      return "border border-blue-400 bg-transparent text-blue-400";

    case inverse && primary:
      return "bg-white text-primary-500 dark:text-primary-400";

    case green:
      return "bg-green-500 text-white";
    case blue:
      return "bg-blue-500 text-blue-700";
    case primary:
      return "bg-primary-500 text-white dark:bg-primary-400";
    case primaryGradient:
      return "button__gradient bg-primary-500 text-white dark:bg-primary-400";
    case lightBlue:
      return "bg-primary-110 text-neutral-710";
    case red:
      return "bg-red-600 text-white dark:bg-red-600";
    case transparent:
      return "bg-transparent";

    default:
      return "bg-white text-neutral-500 dark:bg-neutral-800";
  }
}
